export const COMMERCE_PORTAL_ACCESS_STATUS = {
    NOT_PROVISIONED: "NOT_PROVISIONED",
    PENDING_ACTIVATION: "PENDING_ACTIVATION",
    INVITATION_EXPIRED: "INVITATION_EXPIRED",
    ACTIVE: "ACTIVE",
    BLOCKED: "BLOCKED",
};

const STATUS_PRESENTATION = {
    [COMMERCE_PORTAL_ACCESS_STATUS.NOT_PROVISIONED]: {
        label: "Sin acceso",
        actionLabel: "Habilitar acceso",
        color: "default",
    },
    [COMMERCE_PORTAL_ACCESS_STATUS.PENDING_ACTIVATION]: {
        label: "Invitación pendiente",
        actionLabel: "Reenviar invitación",
        color: "warning",
    },
    [COMMERCE_PORTAL_ACCESS_STATUS.INVITATION_EXPIRED]: {
        label: "Invitación expirada",
        actionLabel: "Reenviar invitación",
        color: "error",
    },
    [COMMERCE_PORTAL_ACCESS_STATUS.ACTIVE]: {
        label: "Activo",
        actionLabel: null,
        color: "success",
    },
    [COMMERCE_PORTAL_ACCESS_STATUS.BLOCKED]: {
        label: "Bloqueado",
        actionLabel: null,
        color: "error",
    },
};

const VALID_STATUSES = new Set(Object.values(COMMERCE_PORTAL_ACCESS_STATUS));
const VALID_INVITATION_RESULTS = new Set([
    "CREATED",
    "RESENT",
    "ALREADY_ACTIVE",
]);
const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const sanitizeEmbeddedPortalAccess = (portalAccess) => {
    if (!portalAccess || !VALID_STATUSES.has(portalAccess.status)) {
        return {
            status: "UNAVAILABLE",
        };
    }

    const sanitized = {
        status: portalAccess.status,
    };
    if (typeof portalAccess.usernameMasked === "string") {
        sanitized.usernameMasked = portalAccess.usernameMasked;
    }
    if (typeof portalAccess.invitationExpiresAt === "string") {
        sanitized.invitationExpiresAt = portalAccess.invitationExpiresAt;
    }
    return sanitized;
};

const sanitizeInvitationResponse = (item, expectedCommerceUuid) => {
    if (
        !item ||
        item.commerceUuid !== expectedCommerceUuid ||
        !VALID_STATUSES.has(item.status)
    ) {
        throw new Error("Respuesta de estados inválida");
    }

    const sanitized = {
        commerceUuid: item.commerceUuid,
        status: item.status,
    };
    if (typeof item.usernameMasked === "string") {
        sanitized.usernameMasked = item.usernameMasked;
    }
    if (typeof item.invitationExpiresAt === "string") {
        sanitized.invitationExpiresAt = item.invitationExpiresAt;
    }
    if (VALID_INVITATION_RESULTS.has(item.result)) {
        sanitized.result = item.result;
    }
    return sanitized;
};

export const getCommercePortalAccessPresentation = (status) =>
    STATUS_PRESENTATION[status] || {
        label: "Estado no disponible",
        actionLabel: null,
        color: "default",
    };

export const COMMERCE_PORTAL_ACCESS_ELIGIBILITY_MESSAGE = "Correo inválido";

export const normalizePortalEmail = (portalEmail) =>
    typeof portalEmail === "string" ? portalEmail.trim() : "";

export const isValidPortalEmail = (portalEmail) => {
    const email = normalizePortalEmail(portalEmail);
    return Boolean(
        email &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );
};

export const inviteCommerceAdmin = async (
    httpClient,
    commerceUuid,
    portalEmail
) => {
    const response = await httpClient.post(
        "/v1/internal/commerce-users/admins/invitations",
        {
            commerceUuid,
            portalEmail: normalizePortalEmail(portalEmail),
        }
    );
    const sanitized = sanitizeInvitationResponse(response.data, commerceUuid);
    if (!VALID_INVITATION_RESULTS.has(response.data?.result)) {
        throw new Error("Respuesta de invitación inválida");
    }
    return {
        ...sanitized,
        result: response.data.result,
    };
};

export const getCommercePortalAccessErrorMessage = (error) => {
    const status = error?.response?.status;

    if (status === 400) {
        return "El correo ingresado no es válido para habilitar el acceso.";
    }
    if (status === 404) {
        return "No se encontró el comercio seleccionado.";
    }
    if (status === 409) {
        return "El correo o el comercio ya están asociados a otra cuenta.";
    }
    if (status === 429) {
        const retryAfterSeconds =
            error?.response?.data?.retryAfterSeconds ||
            error?.response?.headers?.["retry-after"];
        const numericRetryAfter = Number(retryAfterSeconds);
        return Number.isFinite(numericRetryAfter) && numericRetryAfter > 0
            ? `Espera ${numericRetryAfter} segundos antes de reenviar la invitación.`
            : "Espera un momento antes de reenviar la invitación.";
    }

    return "No fue posible actualizar el acceso al portal. Intenta nuevamente.";
};
